import { useState, useEffect } from 'react';
import { AlertTriangle, Award, TrendingUp, DollarSign, Clock, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MechanicPerformanceBanner({ user }) {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only fetch for mechanics
    if (!user || !['mechanic', 'technician', 'floor_manager'].includes(user.role)) {
      setLoading(false);
      return;
    }

    fetchPerformance();
  }, [user]);

  const fetchPerformance = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mechanic/my-performance`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPerformanceData(data);
      }
    } catch (error) {
      console.error('Failed to fetch performance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render for non-mechanics or while loading
  if (loading || !user || !['mechanic', 'technician', 'floor_manager'].includes(user.role)) {
    return null;
  }

  // Don't render if no data or status is normal
  if (!performanceData || (performanceData.status === 'normal' && !dismissed)) {
    return null;
  }

  // Critical warning - cannot be dismissed
  if (performanceData.status === 'critical') {
    return (
      <div className="mb-6" data-testid="mechanic-performance-banner">
        <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200 shadow-lg">
          <div className="p-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 text-lg mb-1">
                  Attention Required: Below Target Hours
                </h3>
                <p className="text-red-700 text-sm">
                  {performanceData.status_message}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/60 rounded-lg p-3">
                    <div className="text-red-600 font-medium">Yesterday</div>
                    <div className="text-2xl font-bold text-red-800">
                      {performanceData.yesterday.hours}h
                      <span className="text-sm font-normal text-red-600 ml-1">
                        / {performanceData.yesterday.target}h target
                      </span>
                    </div>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <div className="text-red-600 font-medium">Previous Week</div>
                    <div className="text-2xl font-bold text-red-800">
                      {performanceData.previous_week.hours}h
                      <span className="text-sm font-normal text-red-600 ml-1">
                        / {performanceData.previous_week.target}h target
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Excellent/Good - can be dismissed
  if ((performanceData.status === 'excellent' || performanceData.status === 'good') && !dismissed) {
    const hasBonus = performanceData.monthly_bonus.bonus_hours > 0;
    
    return (
      <div className="mb-6" data-testid="mechanic-performance-banner">
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 shadow-lg">
          <div className="p-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 bg-emerald-100 rounded-full">
                <Award className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-emerald-800 text-lg mb-1">
                      {hasBonus ? 'Outstanding Performance!' : 'Great Work!'}
                    </h3>
                    <p className="text-emerald-700 text-sm">
                      {performanceData.status_message}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                    onClick={() => setDismissed(true)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {hasBonus && (
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <Clock className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
                      <div className="text-emerald-600 font-medium text-xs">Bonus Hours</div>
                      <div className="text-xl font-bold text-emerald-800">
                        {performanceData.monthly_bonus.bonus_hours}h
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <DollarSign className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
                      <div className="text-emerald-600 font-medium text-xs">Earned This Month</div>
                      <div className="text-xl font-bold text-emerald-800">
                        ${performanceData.monthly_bonus.bonus_amount}
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
                      <div className="text-emerald-600 font-medium text-xs">This Week</div>
                      <div className="text-xl font-bold text-emerald-800">
                        {performanceData.current_week.hours}h
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return null;
}


// Compact version for sidebar or smaller areas
export function MechanicPerformanceCard({ user }) {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !['mechanic', 'technician', 'floor_manager'].includes(user.role)) {
      setLoading(false);
      return;
    }

    fetchPerformance();
  }, [user]);

  const fetchPerformance = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mechanic/my-performance`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPerformanceData(data);
      }
    } catch (error) {
      console.error('Failed to fetch performance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !user || !['mechanic', 'technician', 'floor_manager'].includes(user.role)) {
    return null;
  }

  if (!performanceData) {
    return null;
  }

  const hasBonus = performanceData.monthly_bonus.bonus_hours > 0;

  return (
    <Card className="p-4" data-testid="mechanic-performance-card">
      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-blue-600" />
        My Performance
      </h3>
      
      <div className="space-y-3">
        {/* Current Week Progress */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">This Week</span>
            <span className="font-medium">
              {performanceData.current_week.hours}h / {performanceData.current_week.target}h
            </span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${
                performanceData.current_week.hours >= performanceData.current_week.target 
                  ? 'bg-emerald-500' 
                  : performanceData.current_week.hours >= performanceData.current_week.target * 0.6
                    ? 'bg-amber-500'
                    : 'bg-red-500'
              }`}
              style={{ 
                width: `${Math.min(100, (performanceData.current_week.hours / performanceData.current_week.target) * 100)}%` 
              }}
            />
          </div>
        </div>

        {/* Monthly Bonus */}
        {hasBonus && (
          <div className="bg-emerald-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-emerald-600 font-medium">Monthly Bonus</div>
                <div className="text-lg font-bold text-emerald-700">
                  ${performanceData.monthly_bonus.bonus_amount}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-emerald-600">Bonus Hours</div>
                <div className="font-semibold text-emerald-700">
                  {performanceData.monthly_bonus.bonus_hours}h
                </div>
              </div>
            </div>
            <div className="text-xs text-emerald-600 mt-1">
              ${performanceData.monthly_bonus.rate_per_hour}/hr after 40h/week
            </div>
          </div>
        )}

        {/* Yesterday Stats */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <div className="text-slate-500 text-xs">Yesterday</div>
            <div className={`font-semibold ${performanceData.yesterday.met_target ? 'text-emerald-600' : 'text-red-600'}`}>
              {performanceData.yesterday.hours}h
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <div className="text-slate-500 text-xs">Jobs This Week</div>
            <div className="font-semibold text-slate-700">
              {performanceData.current_week.jobs}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
